import { Controller } from "@hotwired/stimulus";
import { scrollSheetToTop } from "../utils/sheet_scroll";

// Connects to data-controller="referral-wizard"
//
// Drives the 3-step referral-creation sheet: client-side step switching, the
// Step 1 org picker (source tabs + filter + directory search), and persisting
// the chosen org into the hidden referral fields (auto-saved via the sibling
// `auto-save` controller, triggered by the `referral-wizard:persist` event).
export default class extends Controller {
  static targets = [
    "back",
    "title",
    "step",
    "footer",
    "primary",
    "primaryLabel",
    "sendButton",
    "orgChip",
    "orgChipName",
    "orgChipBadge",
    "sourceTab",
    "sourceSection",
    "search",
    "directoryResults",
    "addOrgDefault",
    "addOrgPanel",
    "addOrgCard",
    "orgCard",
    "messageField",
    "requestedUserFrame",
    "servicesFrame",
    "shareFrame",
    "documentsNoteFrame",
    "receivingOrgField",
    "hsdsOrgField",
    "hsdsLocationField",
  ];

  static values = {
    step: { type: Number, default: 1 },
    searchUrl: String,
    servicesUrl: String,
  };

  declare backTarget: HTMLElement;
  declare titleTarget: HTMLElement;
  declare stepTargets: HTMLElement[];
  declare footerTarget: HTMLElement;
  declare primaryTarget: HTMLButtonElement;
  declare primaryLabelTarget: HTMLElement;
  declare sendButtonTarget: HTMLInputElement;
  declare orgChipTarget: HTMLElement;
  declare orgChipNameTarget: HTMLElement;
  declare orgChipBadgeTarget: HTMLElement;
  declare sourceTabTargets: HTMLElement[];
  declare sourceSectionTargets: HTMLElement[];
  declare searchTarget: HTMLInputElement;
  declare directoryResultsTarget: HTMLElement;
  declare addOrgDefaultTarget: HTMLElement;
  declare addOrgPanelTarget: HTMLElement;
  declare addOrgCardTarget: HTMLElement;
  declare orgCardTargets: HTMLElement[];
  declare hasMessageFieldTarget: boolean;
  declare messageFieldTarget: HTMLTextAreaElement;
  declare hasRequestedUserFrameTarget: boolean;
  declare requestedUserFrameTarget: HTMLElement;
  declare hasServicesFrameTarget: boolean;
  declare servicesFrameTarget: HTMLElement;
  declare hasShareFrameTarget: boolean;
  declare shareFrameTarget: HTMLElement;
  declare hasDocumentsNoteFrameTarget: boolean;
  declare documentsNoteFrameTarget: HTMLElement;
  declare receivingOrgFieldTarget: HTMLInputElement;
  declare hsdsOrgFieldTarget: HTMLInputElement;
  declare hsdsLocationFieldTarget: HTMLInputElement;

  declare stepValue: number;
  declare searchUrlValue: string;
  declare servicesUrlValue: string;

  declare sent: boolean;
  declare activeSource: string;
  declare searchTimeout: ReturnType<typeof setTimeout> | undefined;
  declare orgName: string;
  declare directoryHintHtml: string;
  declare frameSkeletons: Map<HTMLElement, string>;

  connect() {
    this.sent = false;
    this.activeSource = "preferred";
    this.orgName = "";
    this.directoryHintHtml = this.directoryResultsTarget.innerHTML;
    // Snapshot each org-dependent frame's initial markup (the loading skeleton)
    // before restoreSelection() can repoint it, so loadServices() can re-stamp
    // the skeleton on every org change — not just the first populate.
    this.snapshotFrameSkeletons();
    this.restoreSelection();
    this.setSourceKey(this.activeSource);
    this.render();
  }

  private snapshotFrameSkeletons() {
    this.frameSkeletons = new Map();
    [
      this.hasRequestedUserFrameTarget && this.requestedUserFrameTarget,
      this.hasServicesFrameTarget && this.servicesFrameTarget,
      this.hasShareFrameTarget && this.shareFrameTarget,
      this.hasDocumentsNoteFrameTarget && this.documentsNoteFrameTarget,
    ].forEach((frame) => {
      if (frame) this.frameSkeletons.set(frame, frame.innerHTML);
    });
  }

  // ---- Steps ----

  primary() {
    if (this.stepValue < 3) {
      this.goToStep(this.stepValue + 1);
    } else {
      this.submit();
    }
  }

  // Every step change goes through here: the sheet body is one scroll container
  // shared by all three steps, so a step arriving has to start at its top.
  private goToStep(step: number) {
    this.stepValue = step;
    this.render();
    scrollSheetToTop(this.element);
  }

  // Final step: flip on the send flag and submit the form for real. The server
  // sends the referral and renders the in-sheet success screen into sheet_frame.
  private submit() {
    if (this.sent) return;
    this.sent = true;
    this.primaryTarget.disabled = true;
    this.sendButtonTarget.disabled = false;
    (this.element as HTMLFormElement).requestSubmit();
  }

  back() {
    if (this.stepValue > 1) {
      this.goToStep(this.stepValue - 1);
    }
  }

  changeOrg() {
    this.goToStep(1);
  }

  private get hasSelection(): boolean {
    return (
      !!this.receivingOrgFieldTarget.value || !!this.hsdsOrgFieldTarget.value
    );
  }

  private render() {
    const step = this.stepValue;

    this.stepTargets.forEach((el) => {
      el.classList.toggle("hidden", el.dataset.step !== String(step));
    });

    const active = this.stepTargets.find((el) => el.dataset.step === String(step));
    if (active) {
      this.titleTarget.textContent = active.dataset.stepTitle || "";
      this.primaryLabelTarget.textContent = active.dataset.stepCta || "";
    }

    // Back button hidden on step 1.
    this.backTarget.classList.toggle("hidden", step <= 1);
    this.backTarget.classList.toggle("inline-flex", step > 1);

    // Org chip shows on steps 2 & 3.
    this.orgChipTarget.classList.toggle("hidden", step === 1);
    this.orgChipTarget.classList.toggle("flex", step !== 1);

    // Continue/Send is gated per step: a selection on step 1, a message on steps
    // 2 and 3 (a message stays required through Send, so a cleared one can't slip
    // past the final step).
    if (step === 1) {
      this.primaryTarget.disabled = !this.hasSelection;
    } else {
      this.primaryTarget.disabled = !this.hasMessage;
    }
  }

  // A failed send (validation rejected → 422) leaves the referral in draft with
  // the sheet open and a flash toast; reset so the user can fix it and retry
  // instead of being stranded behind the disabled button and the `sent` guard. A
  // successful send swaps the sheet for the success screen, disconnecting us.
  onSubmitEnd(event: CustomEvent<{ success: boolean }>) {
    if (this.sent && !event.detail.success) {
      this.sent = false;
      this.sendButtonTarget.disabled = true;
      this.render();
    }
  }

  // Re-evaluate the step-2 gate as the message is typed.
  validateStep() {
    if (this.stepValue === 2) this.render();
  }

  private get hasMessage(): boolean {
    return (
      this.hasMessageFieldTarget && this.messageFieldTarget.value.trim() !== ""
    );
  }

  // ---- Source tabs ----

  setSource(event: Event) {
    const key = (event.currentTarget as HTMLElement).dataset.source || "preferred";
    this.setSourceKey(key);
  }

  private setSourceKey(key: string, clearSearch = true) {
    this.activeSource = key;

    this.sourceTabTargets.forEach((tab) => {
      const isActive = tab.dataset.source === key;
      tab.toggleAttribute("data-active", isActive);
    });

    this.sourceSectionTargets.forEach((section) => {
      const isActive = section.dataset.source === key;
      section.classList.toggle("hidden", !isActive);
      section.classList.toggle("flex", isActive);
    });

    if (clearSearch) this.searchTarget.value = "";
    this.updateAddOrgVisibility();
  }

  // The "Add an organization" card only appears once the user has typed
  // something to search — it's the "can't find it?" fallback to a search.
  private updateAddOrgVisibility() {
    const hasQuery = this.searchTarget.value.trim() !== "";
    this.addOrgCardTarget.classList.toggle("hidden", !hasQuery);
    this.addOrgCardTarget.classList.toggle("block", hasQuery);
  }

  // ---- Search ----

  // The search box always searches the HSDS directory. Typing in any tab jumps
  // to the "All" (directory) tab and runs the server search.
  onSearch(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    if (this.activeSource !== "directory") this.setSourceKey("directory", false);
    this.updateAddOrgVisibility();
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => this.directorySearch(value), 300);
  }

  private async directorySearch(query: string) {
    if (query.trim().length < 2) {
      this.directoryResultsTarget.innerHTML = this.directoryHintHtml;
      return;
    }
    const url = new URL(this.searchUrlValue, window.location.origin);
    url.searchParams.set("q", query);
    const response = await fetch(url.toString(), {
      headers: { Accept: "text/html" },
      credentials: "same-origin",
    });
    if (!response.ok) return;
    this.directoryResultsTarget.innerHTML = await response.text();
  }

  // ---- Add an organization (Google Places) ----

  openAddOrg() {
    this.addOrgDefaultTarget.classList.add("hidden");
    this.addOrgPanelTarget.classList.remove("hidden");
    this.addOrgPanelTarget.classList.add("flex");

    // Carry the query the user already typed into the Places input and focus it
    // so they don't retype; dispatching `input` kicks off the Places search.
    const placeInput = this.addOrgPanelTarget.querySelector<HTMLInputElement>(
      '[data-referral-place-search-target="input"]',
    );
    if (placeInput) {
      placeInput.value = this.searchTarget.value.trim();
      placeInput.focus();
      placeInput.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }

  closeAddOrg() {
    this.addOrgPanelTarget.classList.add("hidden");
    this.addOrgPanelTarget.classList.remove("flex");
    this.addOrgDefaultTarget.classList.remove("hidden");
  }

  // ---- Selection ----

  selectOrg(event: Event) {
    const card = event.currentTarget as HTMLElement;
    this.receivingOrgFieldTarget.value = card.dataset.receivingOrganizationId || "";
    this.hsdsOrgFieldTarget.value = card.dataset.hsdsOrganizationId || "";
    // Always assign so switching from a location-level card to an org-level
    // card clears the stale site rather than keeping the previous one.
    this.hsdsLocationFieldTarget.value = card.dataset.hsdsLocationId || "";
    this.orgName = card.dataset.orgName ? this.titleCase(card.dataset.orgName) : "";

    // The card carries the display name lowercased for filtering; use the
    // rendered name from the card body for the chip.
    const nameEl = card.querySelector(".font-medium");
    const displayName = nameEl?.textContent?.trim() || this.orgName;
    this.orgName = displayName;

    this.markSelected(card);
    this.fillChip(card, displayName);
    this.loadServices(card.dataset.hsdsOrganizationId, card.dataset.hsdsLocationId);

    // Selection only happens on the step-1 picker, so enable Continue.
    this.primaryTarget.disabled = false;
    this.dispatch("persist");
  }

  // Stream the chosen org's details into the org-dependent frames so they're
  // ready by the time the user advances. The Step 2 requested-person picker and
  // services list and the Step 3 share branch + documents note all load from
  // the same action into separate frames. Re-selecting a different org reloads
  // them, keeping Step 3's tier in sync with the selected org.
  private loadServices(hsdsOrganizationId?: string, hsdsLocationId?: string) {
    if (!this.servicesUrlValue || !hsdsOrganizationId) return;
    const url = new URL(this.servicesUrlValue, window.location.origin);
    url.searchParams.set("hsds_organization_id", hsdsOrganizationId);
    if (hsdsLocationId) url.searchParams.set("hsds_location_id", hsdsLocationId);
    const src = url.toString();
    if (this.hasRequestedUserFrameTarget) this.reloadFrame(this.requestedUserFrameTarget, src);
    if (this.hasServicesFrameTarget) this.reloadFrame(this.servicesFrameTarget, src);
    if (this.hasShareFrameTarget) this.reloadFrame(this.shareFrameTarget, src);
    if (this.hasDocumentsNoteFrameTarget) this.reloadFrame(this.documentsNoteFrameTarget, src);
  }

  // Restore the skeleton before navigating so re-selecting a different org shows
  // the loading state, not the previous org's now-stale content, during fetch.
  private reloadFrame(frame: HTMLElement, src: string) {
    const skeleton = this.frameSkeletons.get(frame);
    if (skeleton !== undefined) frame.innerHTML = skeleton;
    frame.setAttribute("src", src);
  }

  private markSelected(selected: HTMLElement) {
    this.orgCardTargets.forEach((card) => {
      card.toggleAttribute("data-selected", card === selected);
    });
  }

  private fillChip(card: HTMLElement, name: string) {
    this.orgChipNameTarget.textContent = name;
    const badge = card.querySelector('[data-role="badge"]');
    this.orgChipBadgeTarget.innerHTML = badge ? badge.innerHTML : "";
  }

  private restoreSelection() {
    if (!this.hasSelection) return;
    const receiving = this.receivingOrgFieldTarget.value;
    const hsds = this.hsdsOrgFieldTarget.value;
    const location = this.hsdsLocationFieldTarget.value;
    const match = this.orgCardTargets.find(
      (card) =>
        (card.dataset.receivingOrganizationId || "") === receiving &&
        (card.dataset.hsdsOrganizationId || "") === hsds &&
        (card.dataset.hsdsLocationId || "") === location,
    );
    if (!match) return;
    this.markSelected(match);
    const nameEl = match.querySelector(".font-medium");
    this.fillChip(match, nameEl?.textContent?.trim() || "");
    this.loadServices(match.dataset.hsdsOrganizationId, match.dataset.hsdsLocationId);
  }

  private titleCase(value: string): string {
    return value.replace(/\b\w/g, (c) => c.toUpperCase());
  }
}
